import { useAtomValue } from "jotai";
import { Col, Input, Row } from "reactstrap";
import SubitemSection from "./SubitemSection";
import SingleReferenceSection from "./SingleReferenceSection";
import { useNavigate } from "react-router-dom";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { useState } from "react";
import PopoverDropdownMenu from "../../Menus/PopoverDropdownMenu";
import DropdownMenuDivider from "../../Menus/DropdownMenuDivider";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import StrUtils from "../../../utils/StrUtils";

interface Props {
  availableFields: any[];
  subitemFields: any[];
  singleReferenceFields: any[];
  moduleAPIName: string;
  onColumnAdd: (field: any) => void;
  description?: string;
}

/**
 * AddColumnOptions - Popover containing fields to add to table, link to field management
 *
 * @param {Array<Field>} - availableFields - Array of remaining fields
 * @param {function} - onColumnAdd - Callback function for when a field (column) is added
 */
const AddColumnOptions = ({
  availableFields,
  subitemFields,
  singleReferenceFields,
  moduleAPIName,
  onColumnAdd,
  description,
}: Props) => {
  const navigate = useNavigate();
  const tenantID = useAtomValue(tenantIDAtom);
  const [searchValue, setSearchValue] = useState("");

  const filteredFields = searchValue
    ? availableFields?.filter((f) =>
        f.Name.toLowerCase().includes(searchValue.toLowerCase())
      )
    : availableFields;

  return (
    <>
      <PopoverDropdownMenu>
        <h5 className="px-2 pt-2 text-dark">
          <i className="mdi mdi-plus-circle-outline pr-2"></i>
          {description || "Add Field"}
        </h5>
        <DropdownMenuDivider />
        <div className="px-2 pt-1 pb-2">
          <form onSubmit={handleSubmit}>
            <Input
              autoFocus
              placeholder="Search Fields"
              bsSize="sm"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </form>
        </div>
        <div
          style={{
            maxHeight: "300px",
            overflowY: "scroll",
            overflowX: "hidden",
          }}
        >
          <Row className="align-items-center">
            {!availableFields?.length && !subitemFields && (
              <Col xs="12">
                <div className="py-3 px-2">All columns are added to table.</div>
              </Col>
            )}
            {filteredFields?.map((field) => {
              return (
                <Col key={field.APIName} xs="12" md="6">
                  <DropdownOptionWithIcon
                    name={field?.Name}
                    onClick={() => {
                      if (typeof onColumnAdd == "function") {
                        onColumnAdd(field);
                      }
                    }}
                    icon={`${StrUtils.iconForField(field)} text-dark`}
                    actionIcon={"mdi mdi-plus"}
                  />
                </Col>
              );
            })}
          </Row>
          {singleReferenceFields?.map((singleReference) => (
            <SingleReferenceSection
              searchValue={searchValue}
              singleReference={singleReference}
              onColumnAdd={onColumnAdd}
            />
          ))}
          {subitemFields?.map((subitem) => (
            <SubitemSection
              subitem={subitem}
              onColumnAdd={onColumnAdd}
              searchValue={searchValue}
            />
          ))}
        </div>
        <DropdownMenuDivider />
        <DropdownOptionWithIcon
          name="Manage Fields"
          onClick={() => {
            navigate(
              `/clouds/${tenantID}/settings/modules/manage/${moduleAPIName}/fields`
            );
          }}
          icon="mdi mdi-database-cog text-dark"
        />
      </PopoverDropdownMenu>
    </>
  );

  // If there is only one available field result, automatically add it.
  function handleSubmit(e: any) {
    e.preventDefault();

    if (filteredFields?.length !== 1) return;

    onColumnAdd(filteredFields[0]);
    setSearchValue("");
  }
};

export default AddColumnOptions;
